import React, { useMemo, useState } from "react";
import { Box, LoadingOverlay } from "@mantine/core";
import {
  IconAdjustmentsHorizontal,
  IconDownload,
  IconPrinter,
} from "@tabler/icons-react";
import type { ReportCanvasViewProps } from "../v2/canvasTypes";
import CustomizeDrawer from "../v2/customize/CustomizeDrawer";
import { updatedLabel } from "../v2/ReportHeaderActions";
import ReportKpiStrip from "../v2/ReportKpiStrip";
import RunErrorPanel from "../v2/RunErrorPanel";
import { primaryMetric, toCardNodes } from "./cards/cardModel";
import MobileCardList from "./cards/MobileCardList";
import { useCardExpansion } from "./cards/useCardExpansion";
import MobileActionRow from "./MobileActionRow";
import MobileReportHeader from "./MobileReportHeader";
import MobileReportToolbar from "./MobileReportToolbar";
import MobileUnsavedBar from "./MobileUnsavedBar";
import { SheetAction } from "./sheets/MobileActionsSheet";
import MobileSortSheet from "./sheets/MobileSortSheet";
import styles from "./MobileReportCanvas.module.css";

/**
 * The phone layout of a v2 report, rendering the model ReportCanvas owns
 * (draft / save / guard, run, table, export, print): a chip row with sheets,
 * 2-column KPI cards, and the tree as collapsible cards. Customize opens full
 * screen; a floating bar offers Save view / Discard while the view is dirty.
 * Holds only layout state (open sheets, open cards).
 */
export default function MobileReportCanvas({
  canvas,
  definition,
  header,
  onDuplicate,
}: ReportCanvasViewProps) {
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const { draft, view, run, data, settled, table } = canvas;
  const cards = useMemo(
    () => toCardNodes(table.sortedNodes, table.nodes),
    [table.sortedNodes, table.nodes],
  );
  const expansion = useCardExpansion(cards);
  const openCustomize = () => setCustomizeOpen(true);

  const extraActions: SheetAction[] = [
    {
      key: "customize",
      label: "Customize view",
      icon: <IconAdjustmentsHorizontal size={17} aria-hidden />,
      onClick: openCustomize,
    },
    {
      key: "export",
      label: "Export",
      icon: <IconDownload size={17} aria-hidden />,
      onClick: canvas.exportCsv,
      disabled: !settled,
    },
    {
      key: "print",
      label: "Print",
      icon: <IconPrinter size={17} aria-hidden />,
      onClick: canvas.printer.print,
      disabled: !settled,
    },
  ];

  return (
    <>
      <MobileReportHeader
        header={header}
        onDuplicate={onDuplicate}
        duplicateLabel="Duplicate with this view"
        extraActions={extraActions}
        onCustomize={openCustomize}
        meta={data ? updatedLabel(data.generated_at, data.timezone) : null}
        toolbar={
          <MobileReportToolbar
            definition={definition}
            view={view}
            facets={run.data?.facets}
            resolvedRange={run.data?.date_range}
            onUpdate={draft.update}
          />
        }
      />
      <Box className={styles.body} data-dirty={draft.dirty || undefined}>
        <ReportKpiStrip
          kpis={view.kpis}
          metricsByKey={canvas.byKey}
          totals={data?.totals.values ?? null}
          index={table.index}
          loading={run.loading}
        />
        <MobileActionRow
          lineCount={data?.totals.line_count ?? null}
          loading={run.loading}
          expansion={expansion}
          hasCards={cards.length > 0}
          sorted={!!view.sort}
          onOpenSort={() => setSortOpen(true)}
        />
        <Box className={styles.cardArea} data-empty={!run.data || undefined}>
          <LoadingOverlay visible={run.loading} zIndex={4} />
          {run.error ? (
            <RunErrorPanel error={run.error} onRetry={run.retry} />
          ) : (
            run.data && (
              <MobileCardList
                ariaLabel={header.name}
                cards={cards}
                columns={table.columns}
                primary={primaryMetric(table.columns, view.kpis)}
                expansion={expansion}
              />
            )
          )}
        </Box>
      </Box>
      <MobileSortSheet
        opened={sortOpen}
        onClose={() => setSortOpen(false)}
        sort={view.sort}
        labelColumn={table.firstColumnLabel}
        columns={table.columns}
        onChange={(sort) => draft.update({ sort })}
      />
      <CustomizeDrawer
        mobile
        opened={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        definition={definition}
        catalog={canvas.catalog}
        view={view}
        onUpdate={draft.update}
      />
      <MobileUnsavedBar
        dirty={draft.dirty}
        saving={draft.saving}
        onSave={() => void draft.save()}
        onDiscard={draft.discard}
      />
    </>
  );
}
